import { useState, useMemo } from 'react';
import { ArrowRight, Sparkles, FileText, Check, X } from '../components/Icons.jsx';

const reflectionPhrases = [
  'i realised', 'i realized', 'i learned', 'i learnt', 'this taught me',
  'in retrospect', 'i now understand', 'this experience showed me',
  'it dawned on me', 'i came to appreciate', 'this changed my view',
  'looking back', 'made me reflect', 'made me consider'
];

const clinicalWords = [
  'placement', 'shadowed', 'shadow', 'observed', 'patient', 'patients', 'ward',
  'doctor', 'nurse', 'clinic', 'hospital', 'gp', 'consultant', 'surgery',
  'a&e', 'a&amp;e', 'theatre', 'multidisciplinary', 'mdt', 'gp practice',
  'care home', 'hospice', 'st john', 'volunteer'
];

const gmcKeywords = [
  'compassion', 'integrity', 'communication', 'leadership', 'teamwork',
  'lifelong learning', 'professionalism', 'empathy', 'respect',
  'evidence-based', 'reflection', 'responsibility', 'accountability'
];

const jargonCliches = [
  'always wanted to be a doctor', 'from a young age', 'ever since i was',
  'passionate', 'dedicated', 'driven', 'committed to making a difference',
  'caring profession', 'helping people', 'i have always been fascinated',
  'doctors save lives', 'rewarding career'
];

const showDontTellFlags = [
  'i am hard-working', 'i am dedicated', 'i am passionate', 'i am committed',
  'i am empathetic', 'i am a team player', 'i am compassionate'
];

function countMatches(text, phrases) {
  const lower = text.toLowerCase();
  let count = 0;
  for (const p of phrases) {
    const re = new RegExp(`\\b${p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    const m = lower.match(re);
    if (m) count += m.length;
  }
  return count;
}

function pickColor(score) {
  if (score >= 75) return 'var(--live-green)';
  if (score >= 50) return 'var(--brand-purple)';
  if (score >= 25) return '#f59e0b';
  return '#ef4444';
}

export default function PsAnalyzer() {
  const [text, setText] = useState('');
  const [analyzed, setAnalyzed] = useState(false);

  const analysis = useMemo(() => {
    const chars = text.length;
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 4).length;
    const avgSentenceLen = sentences ? Math.round(words / sentences) : 0;

    const reflection = countMatches(text, reflectionPhrases);
    const clinical = countMatches(text, clinicalWords);
    const gmc = countMatches(text, gmcKeywords);
    const jargon = countMatches(text, jargonCliches);
    const tellNotShow = countMatches(text, showDontTellFlags);

    const lengthScore = Math.min(100, Math.round((chars / 4000) * 100));
    const reflectionScore = Math.min(100, reflection * 25);
    const clinicalScore = Math.min(100, clinical * 12);
    const gmcScore = Math.min(100, gmc * 18);
    const jargonScore = Math.max(0, 100 - jargon * 22);
    const showScore = Math.max(0, 100 - tellNotShow * 25);
    const varietyScore = avgSentenceLen >= 12 && avgSentenceLen <= 22 ? 90 : Math.max(40, 90 - Math.abs(17 - avgSentenceLen) * 4);

    const overall = Math.round((lengthScore * 0.05 + reflectionScore * 0.25 + clinicalScore * 0.2 + gmcScore * 0.2 + jargonScore * 0.1 + showScore * 0.1 + varietyScore * 0.1));

    return {
      chars, words, sentences, avgSentenceLen,
      lengthScore, reflectionScore, clinicalScore, gmcScore, jargonScore, showScore, varietyScore, overall,
      reflection, clinical, gmc, jargon, tellNotShow
    };
  }, [text]);

  return (
    <>
      <div className="page-head">
        <h1>Personal statement analyser</h1>
        <p className="subtitle">Paste your draft. Scored on reflection depth, evidence, GMC alignment.</p>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>Your personal statement (or draft)</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your UCAS personal statement here. Anything from 200 to 4,000 characters."
          style={{
            width: '100%', minHeight: 240, marginTop: 8, padding: 16,
            border: '1px solid var(--border-strong)', borderRadius: 12,
            fontSize: 14.5, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical',
            background: '#fff'
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {analysis.chars} / 4000 characters · {analysis.words} words
          </span>
          <button className="btn btn-primary" disabled={analysis.chars < 100} onClick={() => setAnalyzed(true)} style={{ opacity: analysis.chars < 100 ? 0.5 : 1 }}>
            Analyse <ArrowRight width="16" height="16" />
          </button>
        </div>
      </div>

      <ReframeBox />

      {analyzed && (
        <>
          <div className="card" style={{ marginBottom: 18, borderLeft: `5px solid ${pickColor(analysis.overall)}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{analysis.overall} / 100</h2>
              <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>{analysis.words} words · {analysis.sentences} sentences · avg {analysis.avgSentenceLen} words/sentence</span>
            </div>
            <p style={{ margin: '10px 0 0', color: 'var(--text-2)' }}>
              {analysis.overall >= 75 && 'Strong draft. Refine the weakest dimensions below — most applicants stop short of this.'}
              {analysis.overall >= 50 && analysis.overall < 75 && 'Solid foundation, with clear room to grow. The biggest wins are usually in reflection and GMC alignment.'}
              {analysis.overall < 50 && 'Early-stage draft. Focus on the low-scoring sections — they\'re the difference between a generic and an offer-winning PS.'}
            </p>
          </div>

          <div className="parents-grid" style={{ marginBottom: 18 }}>
            <ScoreBar label="Length" score={analysis.lengthScore} note={`${analysis.chars} of the 4000-char limit.`} />
            <ScoreBar label="Reflection depth" score={analysis.reflectionScore} note={`${analysis.reflection} reflection phrase${analysis.reflection === 1 ? '' : 's'} found. Aim for 4+.`} />
            <ScoreBar label="Clinical evidence" score={analysis.clinicalScore} note={`${analysis.clinical} clinical-context word${analysis.clinical === 1 ? '' : 's'}. Doctors want specifics, not feelings.`} />
            <ScoreBar label="GMC alignment" score={analysis.gmcScore} note={`${analysis.gmc} GMC-outcome keyword${analysis.gmc === 1 ? '' : 's'}. Compassion, teamwork, integrity, lifelong learning.`} />
            <ScoreBar label="Cliché avoidance" score={analysis.jargonScore} note={analysis.jargon > 0 ? `${analysis.jargon} cliché${analysis.jargon === 1 ? '' : 's'} flagged — see below.` : 'No clichés flagged. Good.'} />
            <ScoreBar label="Show vs tell" score={analysis.showScore} note={analysis.tellNotShow > 0 ? `${analysis.tellNotShow} "I am [trait]" statement${analysis.tellNotShow === 1 ? '' : 's'} — show with examples.` : 'No "I am X" statements found. Good.'} />
          </div>

          <div className="card" style={{ marginBottom: 18 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>Specific suggestions</h3>
            <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-2)', lineHeight: 1.8 }}>
              {analysis.reflectionScore < 60 && <li><strong>Add explicit reflection.</strong> Use phrases like "this taught me", "I realised", "looking back" — each one tells the reader you processed the experience, not just attended it.</li>}
              {analysis.clinicalScore < 60 && <li><strong>Anchor in concrete clinical context.</strong> Name the setting (ward, GP, hospice), what you saw, and one specific thing the doctor or nurse did.</li>}
              {analysis.gmcScore < 50 && <li><strong>Map to GMC outcomes.</strong> Med schools want signals of compassion, teamwork, integrity, and lifelong learning. Show — don't list — at least two.</li>}
              {analysis.jargon > 0 && <li><strong>Cut the clichés.</strong> "Passionate", "dedicated", "always wanted to be a doctor", "rewarding career" — every admissions tutor has read them 1000+ times. Use specific evidence instead.</li>}
              {analysis.tellNotShow > 0 && <li><strong>Show, don't tell.</strong> "I am compassionate" → describe a moment you sat with a confused patient. The reader concludes you're compassionate from the story.</li>}
              {analysis.avgSentenceLen > 25 && <li><strong>Shorter sentences.</strong> Your average is {analysis.avgSentenceLen} words. Aim for 12–22. Long sentences hide weak content.</li>}
              {analysis.avgSentenceLen < 10 && <li><strong>Vary sentence length.</strong> Choppy short sentences can read as immature. Combine some thoughts for rhythm.</li>}
              {analysis.chars < 3500 && <li><strong>You have room to grow.</strong> You're using {analysis.chars} of the 4000-char limit. Most successful PSes hit 3800+.</li>}
              {analysis.chars > 3990 && <li><strong>Trim ruthlessly.</strong> You're at the absolute limit. Cut anything that doesn't move the story forward.</li>}
            </ul>
          </div>

          <section className="parents-cta">
            <div>
              <h2>Want a human-eye review?</h2>
              <p>Our Medic Mind PS service pairs you with a current medical student who has read 500+ statements. Two rounds of edits, GMC-aligned, returned in 72 hours.</p>
            </div>
            <a className="btn">See PS service <ArrowRight width="16" height="16" /></a>
          </section>
        </>
      )}
    </>
  );
}

function ScoreBar({ label, score, note }) {
  return (
    <div className="parents-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <strong style={{ fontSize: 14 }}>{label}</strong>
        <span style={{ fontWeight: 700, color: pickColor(score) }}>{score}</span>
      </div>
      <div className="progress"><div className="progress-bar" style={{ width: `${score}%`, background: pickColor(score) }} /></div>
      <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>{note}</p>
    </div>
  );
}

function ReframeBox() {
  const [src, setSrc] = useState('');
  const [out, setOut] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });
  const [discipline, setDiscipline] = useState('medicine');

  const submit = async () => {
    if (src.trim().length < 30) {
      setState({ status: 'error', message: 'Paste at least a short paragraph (30+ characters).' });
      return;
    }
    setState({ status: 'loading', message: '' });
    setOut('');
    try {
      const res = await fetch('/api/ps-reframe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: src, discipline })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reframe failed');
      setOut(data.rewrite);
      setState({ status: 'done', message: '' });
    } catch (e) {
      setState({ status: 'error', message: e.message });
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20, borderLeft: '5px solid var(--brand-teal)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 18 }}>Reframe a CV-dump paragraph</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['medicine', 'Medicine'], ['dentistry', 'Dentistry']].map(([id, lbl]) => (
            <button key={id} onClick={() => setDiscipline(id)} style={{
              padding: '6px 10px', borderRadius: 99, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              border: `1px solid ${discipline === id ? 'var(--brand-purple)' : 'var(--border)'}`,
              background: discipline === id ? 'var(--accent-soft)' : '#fff',
              color: discipline === id ? 'var(--brand-deep)' : 'var(--text-2)'
            }}>{lbl}</button>
          ))}
        </div>
      </div>
      <p style={{ margin: '0 0 12px', color: 'var(--text-2)', fontSize: 14 }}>
        Paste a "I did X, then Y, then Z" paragraph. Ash will rewrite it as reflective writing tied to the {discipline === 'medicine' ? 'GMC duties' : 'GDC principles'}.
      </p>
      <textarea value={src} onChange={(e) => setSrc(e.target.value)} rows={5}
        placeholder="e.g. I did a week of work experience at my local GP surgery. I shadowed the doctor and saw patients with diabetes, asthma and depression. I also volunteered at a care home for a year."
        style={{ width: '100%', padding: 14, fontSize: 14, border: '1px solid var(--border-strong)', borderRadius: 10, fontFamily: 'inherit', resize: 'vertical', marginBottom: 10 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{src.length} chars</span>
        <button onClick={submit} disabled={state.status === 'loading'} className="btn btn-primary">
          {state.status === 'loading' ? 'Reframing…' : 'Reframe with Ash'} <ArrowRight width="14" height="14" />
        </button>
      </div>
      {state.status === 'error' && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{state.message}</div>}
      {out && (
        <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--card-soft)', borderRadius: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Reflective rewrite</div>
          <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>{out}</p>
          <button onClick={() => navigator.clipboard?.writeText(out)} className="btn btn-secondary" style={{ marginTop: 12, fontSize: 13 }}>Copy</button>
        </div>
      )}
    </div>
  );
}
